import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  InfiniteData,
} from '@tanstack/react-query';
import { QUERY_KEYS } from 'service/querykeys';
import { HrmNotification, HrmNotificationFilters, SSENotificationEvent } from 'types/notifications';
import { useAppSelector } from 'store';
import {
  getHrmNotifications,
  getHrmUnreadCount,
  markHrmNotificationAsRead,
  markAllHrmNotificationsAsRead,
  bulkMarkHrmNotificationsAsRead,
  deleteHrmNotification,
  clearAllHrmNotifications,
  bulkDeleteHrmNotifications,
  getHrmNotificationStats,
} from './api';

type HrmNotifPage = Awaited<ReturnType<typeof getHrmNotifications>>;

// ─── List (infinite) ──────────────────────────────────────────────────────────

export const useHrmNotificationsQuery = (
  filters?: Omit<HrmNotificationFilters, 'page'>,
) => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useInfiniteQuery<HrmNotifPage, Error, InfiniteData<HrmNotifPage>>({
    queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken, filters],
    queryFn: ({ pageParam = 0 }) =>
      getHrmNotifications({ ...filters, page: pageParam as number, size: 20 }),
    getNextPageParam: (lastPage) => {
      const next = lastPage.currentPage + 1;
      return next < lastPage.totalPages ? next : undefined;
    },
    initialPageParam: 0,
    staleTime: 30_000,
    enabled: !!accessToken,
  });
};

// ─── Unread Count ─────────────────────────────────────────────────────────────

export const useHrmUnreadCountQuery = () => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useQuery({
    queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, accessToken],
    queryFn: getHrmUnreadCount,
    staleTime: 15_000,
    enabled: !!accessToken,
  });
};

// ─── Stats ────────────────────────────────────────────────────────────────────

export const useHrmNotificationStatsQuery = () => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useQuery({
    queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS, 'STATS', accessToken],
    queryFn: getHrmNotificationStats,
    staleTime: 15_000,
    enabled: !!accessToken,
  });
};

// ─── Mark single as read ──────────────────────────────────────────────────────

export const useHrmMarkAsReadMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: (uuid: string) => markHrmNotificationAsRead(uuid),
    onMutate: async (uuid) => {
      const queryKey = [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken];
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<InfiniteData<HrmNotifPage>>(queryKey);
      queryClient.setQueriesData<InfiniteData<HrmNotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((n) => n.uuid === uuid ? { ...n, isRead: true } : n),
            })),
          };
        },
      );
      queryClient.setQueryData<number>(
        [...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, accessToken],
        (count) => (count !== undefined ? Math.max(0, count - 1) : count),
      );
      return { prev };
    },
    onError: (_err, _uuid, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData([...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken], ctx.prev);
      }
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, accessToken] });
    },
  });
};

// ─── Mark all as read ─────────────────────────────────────────────────────────

export const useHrmMarkAllAsReadMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: markAllHrmNotificationsAsRead,
    onMutate: async () => {
      const queryKey = [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken];
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueriesData<InfiniteData<HrmNotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((n) => ({ ...n, isRead: true })),
            })),
          };
        },
      );
      queryClient.setQueryData([...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, accessToken], 0);
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, accessToken] });
    },
  });
};

// ─── Bulk Mark as Read ────────────────────────────────────────────────────────

export const useHrmBulkMarkAsReadMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: (uuids: string[]) => bulkMarkHrmNotificationsAsRead(uuids),
    onMutate: async (uuids) => {
      const queryKey = [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken];
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<InfiniteData<HrmNotifPage>>(queryKey);
      const uuidSet = new Set(uuids);
      queryClient.setQueriesData<InfiniteData<HrmNotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((n) => uuidSet.has(n.uuid) ? { ...n, isRead: true } : n),
            })),
          };
        },
      );
      return { prev };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, accessToken] });
    },
  });
};

// ─── Delete single ────────────────────────────────────────────────────────────

export const useHrmDeleteNotificationMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: (uuid: string) => deleteHrmNotification(uuid),
    onMutate: async (uuid) => {
      const queryKey = [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken];
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<InfiniteData<HrmNotifPage>>(queryKey);
      queryClient.setQueriesData<InfiniteData<HrmNotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          let wasUnread = false;
          const pages = old.pages.map((page) => ({
            ...page,
            data: page.data.filter((n) => {
              if (n.uuid === uuid) { if (!n.isRead) wasUnread = true; return false; }
              return true;
            }),
          }));
          if (wasUnread) {
            queryClient.setQueryData<number>(
              [...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, accessToken],
              (count) => (count !== undefined ? Math.max(0, count - 1) : count),
            );
          }
          return { ...old, pages };
        },
      );
      return { prev };
    },
    onError: (_err, _uuid, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData([...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken], ctx.prev);
      }
    },
  });
};

// ─── Clear all ────────────────────────────────────────────────────────────────

export const useHrmClearAllMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: clearAllHrmNotifications,
    onMutate: async () => {
      const queryKey = [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken];
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueriesData<InfiniteData<HrmNotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          return { ...old, pages: [{ ...old.pages[0], data: [], totalElements: 0, totalPages: 0 }] };
        },
      );
      queryClient.setQueryData([...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, accessToken], 0);
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, accessToken] });
    },
  });
};

// ─── Bulk Delete ──────────────────────────────────────────────────────────────

export const useHrmBulkDeleteMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: (uuids: string[]) => bulkDeleteHrmNotifications(uuids),
    onMutate: async (uuids) => {
      const queryKey = [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken];
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<InfiniteData<HrmNotifPage>>(queryKey);
      const uuidSet = new Set(uuids);
      queryClient.setQueriesData<InfiniteData<HrmNotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.filter((n) => !uuidSet.has(n.uuid)),
            })),
          };
        },
      );
      return { prev };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, accessToken] });
    },
  });
};

// ─── SSE Cache Handler ────────────────────────────────────────────────────────
// Called by NotificationSSEProvider when module === 'HRM'

export const applyHrmSSEEvent = (
  queryClient: ReturnType<typeof useQueryClient>,
  accessToken: string | null,
  event: SSENotificationEvent,
) => {
  const listKey = [...QUERY_KEYS.HRM_NOTIFICATIONS, accessToken];
  const countKey = [...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, accessToken];

  switch (event.action) {
    case 'CREATED': {
      const newNotif = event.data as HrmNotification;
      if (!newNotif?.uuid) break;

      // Invalidate relevant HRM caches so the user sees fresh data when navigating
      switch (newNotif.notificationType) {
        case 'MISSING_CHECKOUT_REVIEW_REQUIRED':
        case 'MISSING_CHECKOUT_RECORDED':
        case 'MISSING_CHECKOUT_APPROVED':
        case 'MISSING_CHECKOUT_REJECTED':
        case 'MISSING_CHECKOUT_SUPERSEDED':
          // Invalidate attendance records and profile tracker (manager approvals)
          queryClient.invalidateQueries({ queryKey: ['ATTENDANCE_MONTHLY_SUMMARY'] });
          queryClient.invalidateQueries({ queryKey: ['ATTENDANCE_DAY_SUMMARY'] });
          queryClient.invalidateQueries({ queryKey: ['PROFILE_TRACKER_LIST'] });
          break;
      }

      queryClient.setQueriesData<InfiniteData<HrmNotifPage>>(
        { queryKey: listKey, exact: false },
        (old) => {
          if (!old) return old;
          // De-duplicate: remove existing entry with same UUID if present
          const allExisting = old.pages.flatMap((p) => p.data);
          if (allExisting.some((n) => n.uuid === newNotif.uuid)) return old;
          const firstPage = old.pages[0];
          return {
            ...old,
            pages: [
              { ...firstPage, data: [newNotif, ...firstPage.data] },
              ...old.pages.slice(1),
            ],
          };
        },
      );
      if (!newNotif.isRead) {
        queryClient.setQueryData<number>(
          countKey,
          (count) => (count !== undefined ? count + 1 : 1),
        );
      }
      break;
    }

    case 'READ': {
      const uuids = event.notificationUuids ??
        (event.notificationUuid ? [event.notificationUuid] : []);
      if (!uuids.length) break;
      const uuidSet = new Set(uuids);
      queryClient.setQueriesData<InfiniteData<HrmNotifPage>>(
        { queryKey: listKey, exact: false },
        (old) => {
          if (!old) return old;
          let markedCount = 0;
          const pages = old.pages.map((page) => ({
            ...page,
            data: page.data.map((n) => {
              if (uuidSet.has(n.uuid) && !n.isRead) { markedCount++; return { ...n, isRead: true }; }
              return n;
            }),
          }));
          if (markedCount > 0) {
            queryClient.setQueryData<number>(
              countKey,
              (count) => (count !== undefined ? Math.max(0, count - markedCount) : 0),
            );
          }
          return { ...old, pages };
        },
      );
      break;
    }

    case 'READ_ALL':
      queryClient.invalidateQueries({ queryKey: listKey });
      queryClient.setQueryData(countKey, 0);
      break;

    case 'DELETED': {
      const uuids = event.notificationUuids ??
        (event.notificationUuid ? [event.notificationUuid] : []);
      if (!uuids.length) break;
      const uuidSet = new Set(uuids);
      queryClient.setQueriesData<InfiniteData<HrmNotifPage>>(
        { queryKey: listKey, exact: false },
        (old) => {
          if (!old) return old;
          let removedUnread = 0;
          const pages = old.pages.map((page) => ({
            ...page,
            data: page.data.filter((n) => {
              if (uuidSet.has(n.uuid)) { if (!n.isRead) removedUnread++; return false; }
              return true;
            }),
          }));
          if (removedUnread > 0) {
            queryClient.setQueryData<number>(
              countKey,
              (count) => (count !== undefined ? Math.max(0, count - removedUnread) : 0),
            );
          }
          return { ...old, pages };
        },
      );
      break;
    }

    case 'CLEARED':
      queryClient.invalidateQueries({ queryKey: listKey });
      queryClient.setQueryData(countKey, 0);
      break;
  }
};
